import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { BrokerWizard } from '@/features/brokers/BrokerWizard';
import {
  canaisDoAssistente,
  pedidoPublicoDoAssistente,
  soDigitosDoAssistente,
} from '@/features/brokers/assistenteDoCorretor';
import { ApiError } from '@/shared/api/client';
import {
  publicBrokerSignup,
  type BrokerPublicSignupResult,
} from '@/shared/api/resources';
import { ErrorCode } from '@/shared/api/types';
import { Alert } from '@/shared/ui/Alert';
import { LogoHorizontal } from '@/shared/ui/Logo';
import { Spinner } from '@/shared/ui/Spinner';

import { usePortalConfig } from './usePortalConfig';

/**
 * Cadastro publico de corretores (ADR 0095), no endereco que a empresa escolheu na
 * configuracao de acesso, como seguracore.com.br/sudaseg, com a ativacao por e-mail
 * (ADR 0096).
 *
 * A aplicacao pergunta ao servidor se o caminho de um segmento e um cadastro publico.
 * Se nao for, mostra `naoEncontrada`, a pagina nao encontrada de sempre. Se for, mostra
 * o mesmo assistente da seguradora (Buscar CNPJ com a triagem da SUSEP e a Receita,
 * Dados da Corretora, Dados Bancarios e Dados do Usuario), e no fim a corretora e o
 * administrador do portal dela existem, com a senha escolhida aqui, e o acesso espera o
 * clique no link de ativacao enviado ao e-mail informado.
 */
export function BrokerPublicSignupPage({
  naoEncontrada,
}: {
  naoEncontrada: ReactElement;
}) {
  const { enderecoPublico = '' } = useParams();
  const navigate = useNavigate();
  const [concluido, setConcluido] = useState<BrokerPublicSignupResult | null>(null);

  const pagina = useQuery({
    queryKey: ['cadastro-publico', enderecoPublico],
    queryFn: ({ signal }) => publicBrokerSignup.page(enderecoPublico, signal),
    retry: false,
    staleTime: Infinity,
  });
  const dados = pagina.data;
  const { portal } = usePortalConfig(dados?.tenant_slug);
  const reconhecida = portal?.resolved ? portal : null;

  if (pagina.isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner size={28} label="Carregando" className="text-brand-500" />
      </div>
    );
  }
  if (
    pagina.isError &&
    pagina.error instanceof ApiError &&
    pagina.error.code === ErrorCode.NotFound
  ) {
    return naoEncontrada;
  }

  const slug = dados?.tenant_slug ?? '';
  const acesso = slug ? `/entrar/${encodeURIComponent(slug)}` : '/entrar';
  const empresa = reconhecida?.display_name || dados?.tenant_name || 'a seguradora';

  let conteudo;
  if (pagina.isError || !dados) {
    conteudo = (
      <Alert tone="danger" title="Nao foi possivel abrir o cadastro">
        Tente de novo em alguns minutos.
      </Alert>
    );
  } else if (!dados.available) {
    conteudo = (
      <div className="flex flex-col gap-4">
        <Alert tone="info" title="Cadastro indisponivel">
          {dados.message}
        </Alert>
        <Link
          to={acesso}
          className="text-sm font-medium text-brand-600 hover:underline"
        >
          Voltar para o acesso
        </Link>
      </div>
    );
  } else if (concluido) {
    // O acesso so vale depois do clique no link enviado ao e-mail (ADR 0096).
    const enviado = concluido.activation_email_sent !== false;
    conteudo = (
      <div className="flex flex-col gap-4">
        <Alert
          tone={enviado ? 'success' : 'warning'}
          title={
            enviado ? 'Cadastro concluido. Confirme seu e-mail' : 'Cadastro concluido'
          }
        >
          {concluido.message}
        </Alert>
        {enviado ? (
          <p className="text-sm text-ink-600">
            Abra o e-mail enviado para{' '}
            {concluido.email_masked ?? 'o endereco informado'} e clique no link de
            ativacao. O acesso ao portal so vale depois do clique. Se o e-mail nao
            chegar, confira a caixa de spam ou, na tela de acesso, entre com o e-mail e
            a senha para pedir um link novo.
          </p>
        ) : null}
        <div>
          <Link
            to={concluido.login_path}
            className="text-sm font-medium text-brand-600 hover:underline"
          >
            Ir para a tela de acesso
          </Link>
        </div>
      </div>
    );
  } else {
    conteudo = (
      <BrokerWizard<BrokerPublicSignupResult>
        canais={canaisDoAssistente(dados.options.channels)}
        perfis={[]}
        podeAlterarConta
        podeConvidar={false}
        bancos={dados.options.banks}
        verificarCnpj={(cnpj) => publicBrokerSignup.check(enderecoPublico, cnpj)}
        concluir={(pedido, tela) =>
          publicBrokerSignup.complete(enderecoPublico, {
            privacy_acknowledged: tela.acesso.ciente,
            draft: { broker: pedidoPublicoDoAssistente(pedido).broker },
            user: {
              name: tela.acesso.name.trim(),
              email: tela.acesso.email.trim(),
              cpf: soDigitosDoAssistente(tela.acesso.cpf),
              phone: tela.acesso.phone.trim(),
              password: tela.acesso.password,
              password_confirmation: tela.acesso.password_confirmation,
            },
          })
        }
        onConcluido={setConcluido}
        onCancelar={() => void navigate(acesso)}
        acessoImediato={{
          avisoDePrivacidade: (
            <>
              Os dados informados no cadastro (CNPJ, dados da corretora, dados bancarios
              e dados da pessoa que administra o portal) sao usados por {empresa} para
              cadastrar a corretora e criar o acesso dela, a pedido dela (Lei
              13.709/2018, art. 7o, V). O registro da corretora e consultado na SUSEP.
              Versao do aviso: {dados.privacy_notice_version}.
            </>
          ),
        }}
      />
    );
  }

  const logo = reconhecida?.layout.common.logo;
  return (
    <div className="min-h-dvh bg-canvas">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-5 py-4">
          {logo ? (
            <img
              src={logo}
              alt={reconhecida?.display_name ?? ''}
              className="h-10 w-auto"
            />
          ) : (
            <LogoHorizontal height={40} />
          )}
          <span className="text-sm text-ink-500">{empresa}</span>
        </div>
      </header>
      <main className="mx-auto flex max-w-4xl flex-col gap-5 px-5 py-8">
        <div>
          <h1 className="text-xl font-semibold text-ink-900">Cadastro de corretora</h1>
          <p className="mt-1 text-sm text-ink-500">
            Somente corretoras com registro ativo na SUSEP e habilitadas em Seguros de
            Danos podem ser cadastradas. Ao concluir, enviamos um link de ativacao para
            o e-mail informado: o acesso ao portal da corretora vale depois do clique.
          </p>
        </div>
        {conteudo}
      </main>
    </div>
  );
}
